import { AnimatePresence, motion, useTransform } from 'framer-motion';
import { useContext, useState } from 'react';
import styled from 'styled-components';
import { coreTokens } from '../../core';
import { useText } from '../../utils/hooks/use_text';
import { Button } from '../button';
import { IconButton } from '../icon_button';
import { Flex } from '../layout';
import { VisuallyHidden } from '../visually_hidden';
import { downloadFile } from './_download_file';
import { FileViewerContext } from './_file_viewer.context';
import { theme } from './file_viewer.theme';

const elements = {
	actionBar: styled(motion.div)`
		position: absolute;
		z-index: 1;
		bottom: 0;
		left: 0;
		width: 100%;
		height: calc(${coreTokens.spacing.x2} * 2 + ${coreTokens.spacing.x5});
		padding: ${coreTokens.spacing.x2};
		display: flex;
		gap: ${coreTokens.spacing.x1_5};
		justify-content: space-between;
		background: ${theme.getValue('background')};
		box-sizing: border-box;

		${coreTokens.breakpointQuery.small} {
			gap: ${coreTokens.spacing.x2};
			padding: ${coreTokens.spacing.x3};
			height: calc(${coreTokens.spacing.x3} * 2 + ${coreTokens.spacing.x5});
		}

		@media screen and (max-width: 400px) {
			flex-direction: column;
			justify-content: stretch;

			&[data-has-download-button] {
				height: calc(${coreTokens.spacing.x2} * 2 + ${coreTokens.spacing.x5} + 32px);
			}
		}
	`,
	navigation: styled.div`
		display: flex;
		gap: ${coreTokens.spacing.x1};
		align-items: center;
		font: ${coreTokens.text.label.regular};
	`,
	counterSegment: styled.span`
		font-variant-numeric: tabular-nums;
	`,
	navigationCurrent: styled.div`
		width: 100%;
		text-align: center;
		display: flex;
		justify-content: center;
		gap: ${coreTokens.spacing.x1};
	`,
	download: styled.div``,
};

export function CellulaFileViewerActionBar(_: unknown): JSX.Element {
	const { showBar, showBarManually, showDownload, y, currentIndex, files, onDownloadError, goToNext, goToPrevious } =
		useContext(FileViewerContext);
	const maxIndex = files.length - 1;
	const [isDownloading, setIsDownloading] = useState(false);
	const opacity = useTransform(y, [-70, 0, 70], [0, 1, 0]);
	const staticTexts = useText();
	const currentFile = files[currentIndex];

	function handleClickPrev(): void {
		goToPrevious();
	}

	function handleClickNext(): void {
		goToNext();
	}

	async function handleClickDownload(): Promise<void> {
		const item = files[currentIndex];
		if (item == null) return;

		setIsDownloading(true);

		try {
			await downloadFile(item.downloadUrl, item.fileName);
		} catch {
			onDownloadError?.();
		}

		setIsDownloading(false);
	}

	return (
		<AnimatePresence initial={false}>
			{showBar && showBarManually ? (
				<elements.actionBar
					initial={{ y: '100%' }}
					animate={{ y: 0 }}
					exit={{ y: '100%' }}
					transition={{ duration: 0.4, ease: 'backInOut' }}
					style={{ opacity }}
					data-has-download-button={showDownload ?? false}
				>
					<elements.navigation>
						<Flex shrink={0} asChild>
							<IconButton
								icon='chevronLeft'
								size='medium'
								text={staticTexts('Component.FileViewer.PreviousItem')}
								variant='on-dark'
								tooltipVariant='light'
								onClick={handleClickPrev}
							/>
						</Flex>
						<elements.navigationCurrent>
							<elements.counterSegment>{currentIndex + 1}</elements.counterSegment>
							{staticTexts('Component.FileViewer.FileCounter.Of')}
							<elements.counterSegment>{maxIndex + 1}</elements.counterSegment>
						</elements.navigationCurrent>
						<Flex shrink={0} asChild>
							<IconButton
								icon='chevronRight'
								size='medium'
								text={staticTexts('Component.FileViewer.NextItem')}
								variant='on-dark'
								tooltipVariant='light'
								onClick={handleClickNext}
							/>
						</Flex>
					</elements.navigation>
					{showDownload ? (
						<Button icon='download' onClick={handleClickDownload} loading={isDownloading} size='xSmall'>
							{staticTexts('Component.FileViewer.Download')} <VisuallyHidden>: {currentFile.fileName}</VisuallyHidden>
						</Button>
					) : null}
				</elements.actionBar>
			) : null}
		</AnimatePresence>
	);
}
